import type { LayoutInfo } from "../types/templateConfiguration";
import type { IProduct2 } from "../types/general";
import customFetch from "../composables/customFetch";

export default defineNuxtRouteMiddleware(async (to, from) => {
  const config = useRuntimeConfig();
  const layout = config.public.layout as LayoutInfo;
  const discoverType = layout.discoverType ?? null;
  const localePath = useLocalePath();
  const isSupplier = discoverType
    ? discoverType === "supplier"
    : layout.type.includes("supplier");
  if (to.path.startsWith("/discover")) {
    if (isSupplier) {
      return navigateTo(localePath("/experiences"));
    } else {
      return navigateTo(localePath("/things-to-do"));
    }
  } else if (to.fullPath == "/experiences") {
    if (!isSupplier) {
      return navigateTo(localePath("/things-to-do"));
    }
  } else if (to.fullPath == "/things-to-do") {
    if (isSupplier) {
      return navigateTo(localePath("/experiences"));
    }
  } else {
    const pathSegments = to.path.split("/").filter(Boolean);
    const languagePrefixes = [
      "en",
      "fr",
      "es",
      "de",
      "ar",
      "ru",
      "tr",
      "az",
      "zh",
      "it",
      "he",
      "fa",
      "pt",
      "uz",
    ];
    let citySlug, productSlug;
    const hasLang = languagePrefixes.some(
      (prefix) => pathSegments[0] === prefix,
    );
    if (
      (hasLang && pathSegments.length > 2) ||
      (!hasLang && pathSegments.length > 1)
    ) {
      if (hasLang) {
        [, citySlug, productSlug] = pathSegments;
      } else {
        [citySlug, productSlug] = pathSegments;
      }
      // /experiences/{slug} and /things-to-do-in-{city}/{slug} both resolve to the
      // same product page, which is duplicate content for SEO. Whichever one isn't
      // this site's canonical URL scheme gets a permanent (301) redirect to the
      // canonical one instead of being served directly.
      if (citySlug === "experiences" && productSlug && !isSupplier) {
        const cityForProduct = await resolveProductCitySlug(productSlug);
        if (cityForProduct) {
          return navigateTo(
            localePath(`/things-to-do-in-${cityForProduct}/${productSlug}`),
            { redirectCode: 301 },
          );
        }
        throw createError({ statusCode: 404, fatal: true });
      }

      if (citySlug?.startsWith("things-to-do-in-") && productSlug && isSupplier) {
        return navigateTo(localePath(`/experiences/${productSlug}`), {
          redirectCode: 301,
        });
      }

      if (
        citySlug !== "blog" &&
        citySlug !== "experiences" &&
        citySlug !== "auth" &&
        citySlug !== "about" &&
        citySlug !== "cart" &&
        citySlug !== "checkout" &&
        citySlug !== "terms-and-conditions" &&
        citySlug !== "privacy-policy" &&
        citySlug !== "faq" &&
        citySlug !== "things-to-do" &&
        citySlug !== "search" &&
        citySlug !== "invoices" &&
        citySlug !== "coming-soon" &&
        !citySlug.startsWith("things-to-do-in")
      ) {
        if (isSupplier) {
          return navigateTo(`/experiences/${productSlug}`);
        } else {
          return navigateTo(`/things-to-do-in-${citySlug}/${productSlug}`);
        }
      }
    }
  }
});

async function resolveProductCitySlug(
  productSlug: string,
): Promise<string | null> {
  try {
    const product = await customFetch<IProduct2>(
      `api/v2/products/${productSlug}`,
    );
    return product?.cities?.[0]?.slug ?? null;
  } catch {
    return null;
  }
}
